import type { Article } from './articles';
import type { ApiArticle } from './article-input';

function isArticle(value: unknown): value is Article {
  if (typeof value !== 'object' || value === null) return false;
  const article = value as Article;
  return (
    typeof article.slug === 'string' &&
    typeof article.tag === 'string' &&
    typeof article.title === 'string' &&
    typeof article.desc === 'string' &&
    typeof article.readTime === 'number'
  );
}

function isApiArticle(value: unknown): value is ApiArticle {
  if (!isArticle(value)) return false;
  const article = value as ApiArticle;
  return (
    typeof article.intro === 'string' &&
    Array.isArray(article.sections) &&
    Array.isArray(article.summary) &&
    typeof article.cta === 'string' &&
    typeof article.datePublished === 'string' &&
    typeof article.dateModified === 'string' &&
    typeof article.updatedLabel === 'string'
  );
}

export async function fetchApiArticles(): Promise<Article[]> {
  try {
    const response = await fetch('/api/articles');
    if (!response.ok) return [];
    const data: unknown = await response.json();
    if (typeof data !== 'object' || data === null || !('articles' in data)) return [];
    const articles = (data as { articles: unknown }).articles;
    if (!Array.isArray(articles)) return [];
    return articles.filter(isArticle);
  } catch {
    return [];
  }
}

export async function fetchApiArticle(slug: string): Promise<ApiArticle | null> {
  try {
    const response = await fetch(`/api/articles/${encodeURIComponent(slug)}`);
    if (!response.ok) return null;
    const data: unknown = await response.json();
    return isApiArticle(data) ? data : null;
  } catch {
    return null;
  }
}

let serverPrerenderedArticle: ApiArticle | null = null;

export function setPrerenderedArticle(article: ApiArticle | null) {
  serverPrerenderedArticle = article;
}

export function getMatchingPrerenderedArticle(slug: string | undefined): ApiArticle | null {
  const injected =
    typeof window !== 'undefined'
      ? (window as Window & { __PRERENDERED_ARTICLE__?: unknown }).__PRERENDERED_ARTICLE__
      : serverPrerenderedArticle;
  if (!slug || !isApiArticle(injected) || injected.slug !== slug) return null;
  return injected;
}
